import type { RiskPredictionResponse } from '@/api/dataVault';

interface Props {
  data?: RiskPredictionResponse;
  isLoading: boolean;
}

const fmt = (v: number | null | undefined, digits = 1) =>
  v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: digits });

const pct = (v: number | null | undefined) => (v == null ? '—' : `${(v * 100).toFixed(0)}%`);

const usd = (v: number | null | undefined) =>
  v == null ? '—' : `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * Polygon predictions summary, shown when the Polygon product is the active
 * toggle. Mirrors the phreatic panel — surfaces the per-site risk/closure
 * predictions Polygon already produces, so the toggle shows "data + predictions"
 * symmetrically for both products.
 */
export function PolygonPredictionsPanel({ data, isLoading }: Props) {
  if (isLoading) {
    return <div className="text-xs opacity-60 px-1 py-3">Loading polygon predictions…</div>;
  }

  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="text-xs opacity-60 px-1 py-3">
        Select one or more sites to see polygon predictions.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-400/30 overflow-hidden mb-3">
      <div className="px-3 py-2 text-[11px] uppercase tracking-wide opacity-60 border-b border-gray-400/20">
        Polygon Predictions
      </div>
      <table className="w-full text-xs">
        <thead className="opacity-60">
          <tr className="text-left">
            <th className="px-3 py-2">Site</th>
            <th className="px-3 py-2">Risk</th>
            <th className="px-3 py-2 text-right">Max Exceedance Prob.</th>
            <th className="px-3 py-2 text-right">Wells Exceeding</th>
            <th className="px-3 py-2 text-right">Years to Closure</th>
            <th className="px-3 py-2 text-right">Est. Cost to Closure</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.site_id} className="border-t border-gray-400/15">
              <td className="px-3 py-2">{r.site_name}</td>
              <td className="px-3 py-2">{r.risk_category ?? '—'}</td>
              <td className="px-3 py-2 text-right">{pct(r.max_exceedance_probability)}</td>
              <td className="px-3 py-2 text-right">
                {r.wells_exceeding_mcl_likely == null
                  ? '—'
                  : `${r.wells_exceeding_mcl_likely}${r.total_wells_assessed != null ? ` / ${r.total_wells_assessed}` : ''}`}
              </td>
              <td className="px-3 py-2 text-right">{fmt(r.estimated_years_to_closure, 0)}</td>
              <td className="px-3 py-2 text-right">{usd(r.estimated_cost_to_closure)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
